import { useState, useEffect } from 'react';
import { reactionsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const REACTION_CONFIG = {
  agree: { emoji: '👍', label: 'Katılıyorum' },
  great: { emoji: '🔥', label: 'Harika Analiz' },
  thinking: { emoji: '🤔', label: 'Düşündürücü' },
  insightful: { emoji: '💡', label: 'Aydınlatıcı' },
  educational: { emoji: '📚', label: 'Öğretici' },
  applause: { emoji: '👏', label: 'Takdir' },
};

export const Reactions = ({ postId, className }) => {
  const [reactions, setReactions] = useState({
    agree: 0,
    great: 0,
    thinking: 0,
    insightful: 0,
    educational: 0,
    applause: 0,
  });
  const [userReactions, setUserReactions] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    // Basit anonim client id (localStorage tabanlı)
    let storedId = localStorage.getItem('reaction_client_id');
    if (!storedId) {
      storedId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('reaction_client_id', storedId);
    }
    setClientId(storedId);
  }, []);

  useEffect(() => {
    if (!clientId) return;
    
    const fetchReactions = async () => {
      try {
        const data = await reactionsApi.getPostReactions(postId, clientId);
        setReactions(data.reactions);
        setUserReactions(data.user_reactions || []);
      } catch (error) {
        console.error('Failed to fetch reactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReactions();
  }, [postId, clientId]);

  const handleReaction = async (type) => {
    // Aynı kullanıcı aynı yazıda aynı tepkiyi tekrar veremesin
    if (submitting || userReactions.includes(type)) return;
    
    setSubmitting(type);
    try {
      const result = await reactionsApi.addReaction(postId, type, clientId);
      
      if (result.action === 'added') {
        // Monotonik artış: sadece +1, geri alma yok
        setReactions((prev) => ({ ...prev, [type]: prev[type] + 1 }));
        setUserReactions((prev) => [...prev, type]);
      } else if (result.action === 'exists') {
        toast.info('Bu tepkiyi zaten verdiniz.');
      }
    } catch (error) {
      toast.error('Tepki eklenirken bir hata oluştu');
    } finally {
      setSubmitting(null);
    }
  };

  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);

  if (loading) {
    return (
      <div className={cn("flex flex-wrap gap-2", className)}>
        {Object.keys(REACTION_CONFIG).map((type) => (
          <div key={type} className="h-10 w-24 bg-muted animate-pulse rounded-sm" />
        ))}
      </div>
    );
  }

  return (
    <div className={cn("", className)}>
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-semibold tracking-widest uppercase text-muted-foreground">
          Tepkiler
        </span>
        {totalReactions > 0 && (
          <span className="text-xs text-muted-foreground">
            ({totalReactions})
          </span>
        )}
      </div>
      
      <div className="flex flex-wrap gap-2">
        {Object.entries(REACTION_CONFIG).map(([type, { emoji, label }]) => {
          const isActive = userReactions.includes(type);
          const count = reactions[type];
          
          return (
            <Button
              key={type}
              variant="outline"
              size="sm"
              onClick={() => handleReaction(type)}
              disabled={submitting === type || userReactions.includes(type)}
              className={cn(
                "h-auto py-2 px-3 border-border transition-all",
                isActive && "bg-accent/10 border-accent text-accent",
                !isActive && "hover:bg-muted/50"
              )}
            >
              <span className="text-lg mr-1.5">{emoji}</span>
              <span className="text-xs font-medium">{label}</span>
              {count > 0 && (
                <span className={cn(
                  "ml-2 text-xs font-semibold",
                  isActive ? "text-accent" : "text-muted-foreground"
                )}>
                  {count}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default Reactions;
